(() => {
  'use strict';

  const VERSION = '2.2.0';
  if (window.MoztechIlluminatedJourney?.version === VERSION) return;

  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const smoothstep = (start, end, value) => {
    const progress = clamp((value - start) / Math.max(end - start, 0.0001));
    return progress * progress * (3 - 2 * progress);
  };

  class MoztechIlluminatedJourneyInstance {
    constructor(root) {
      this.root = root;
      this.stage = root.querySelector('[data-story-stage]');
      this.intro = root.querySelector('[data-story-intro]');
      this.core = root.querySelector('[data-story-core]');
      this.handoff = root.querySelector('[data-story-handoff]');
      this.items = [...root.querySelectorAll('[data-story-item]')];
      this.mobileQuery = window.matchMedia('(max-width: 768px)');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.isDesignMode = Boolean(
        window.Shopify?.designMode
        || document.documentElement.classList.contains('shopify-design-mode')
      );
      this.destroyed = false;
      this.frame = 0;
      this.progress = -1;
      this.layout = [];
      this.itemRenderStates = new Map();
      this.sectionTop = 0;
      this.sectionHeight = 0;
      this.isNearViewport = !('IntersectionObserver' in window);
      this.onScroll = () => this.queueUpdate();
      this.onResize = () => {
        this.measure();
        this.queueUpdate(true);
      };
      this.onViewportChange = () => {
        this.measure();
        this.queueUpdate(true);
      };

      if (!this.stage) return;

      this.measure();
      this.root.dataset.storyState = this.reducedMotion ? 'reduced-motion' : 'ready';

      if (this.reducedMotion) {
        this.apply(1);
        return;
      }

      if (this.isDesignMode) {
        this.apply(clamp(Number(this.root.dataset.storyEditorProgress) || 0.52));
        this.root.dataset.storyState = 'editor-preview';
        return;
      }

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
      this.mobileQuery.addEventListener?.('change', this.onViewportChange);

      if ('IntersectionObserver' in window) {
        this.viewportObserver = new IntersectionObserver((entries) => {
          this.isNearViewport = entries.some((entry) => entry.isIntersecting);
          this.root.classList.toggle('is-motion-active', this.isNearViewport);
          this.queueUpdate(true);
        }, { rootMargin: '35% 0px', threshold: 0 });
        this.viewportObserver.observe(this.root);
      } else {
        this.root.classList.add('is-motion-active');
      }

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.onResize());
        this.resizeObserver.observe(this.root);
      }

      this.queueUpdate(true);
    }

    measure() {
      if (!this.stage) return;
      const width = Math.max(this.stage.clientWidth, window.innerWidth, 1);
      const height = Math.max(this.stage.clientHeight, window.innerHeight, 1);
      const rootRect = this.root.getBoundingClientRect();
      this.sectionTop = rootRect.top + (window.scrollY || window.pageYOffset || 0);
      this.sectionHeight = Math.max(rootRect.height, this.root.offsetHeight, height);
      const mobile = this.mobileQuery.matches;
      const gatherX = width * 0.5;
      const gatherY = height * 0.48;

      this.layout = this.items.map((item) => {
        const x = clamp(Number(item.dataset[mobile ? 'storyMobileX' : 'storyX']) || 50, 0, 100);
        const y = clamp(Number(item.dataset[mobile ? 'storyMobileY' : 'storyY']) || 50, 0, 100);
        const rotation = clamp(Number(item.dataset.storyRotation) || 0, -32, 32);
        const order = Math.max(1, Number(item.dataset.storyOrder) || 1);
        const opacity = clamp(Number(item.dataset.storyOpacity) || 1, 0.3, 1);
        item.style.left = `${x}%`;
        item.style.top = `${y}%`;
        return {
          item,
          kind: item.dataset.storyKind || 'idea',
          order,
          opacity,
          rotation,
          dx: gatherX - width * x / 100,
          dy: gatherY - height * y / 100
        };
      });
    }

    queueUpdate(force = false) {
      if (this.destroyed || this.reducedMotion || this.isDesignMode) return;
      if (!force && !this.isNearViewport) return;
      if (force) this.progress = -1;
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.update();
      });
    }

    update() {
      if (this.destroyed) return;
      const viewportHeight = Math.max(window.innerHeight, 1);
      const scrollTop = window.scrollY || window.pageYOffset || 0;
      const sectionTop = this.sectionTop - scrollTop;
      const scrollRange = Math.max(this.sectionHeight - viewportHeight, viewportHeight * 0.5);
      const progress = clamp(-sectionTop / scrollRange);

      if (!this.isNearViewport && progress === this.progress) return;
      this.apply(progress);
    }

    apply(progress) {
      const normalized = clamp(progress);
      this.progress = normalized;
      this.root.style.setProperty('--story-progress', normalized.toFixed(4));
      this.root.dataset.storyProgress = normalized.toFixed(3);

      const introReveal = smoothstep(0.01, 0.12, normalized);
      const introExit = smoothstep(0.16, 0.28, normalized);
      const introOpacity = introReveal * (1 - introExit);
      if (this.intro) {
        const opacity = introOpacity.toFixed(3);
        const transform = `translate3d(0, ${((1 - introReveal) * 22 - introExit * 24).toFixed(1)}px, 0)`;
        const state = `${opacity}|${transform}`;
        if (this.introRenderState !== state) {
          this.intro.style.opacity = opacity;
          this.intro.style.transform = transform;
          this.introRenderState = state;
        }
      }

      const gather = smoothstep(0.60, 0.94, normalized);
      const filterGather = Math.round(gather * 12) / 12;
      const handoffGather = gather * 0.82;
      const dissolve = smoothstep(0.975, 1, normalized);
      const mobile = this.mobileQuery.matches;

      this.layout.forEach(({ item, kind, order, opacity, rotation, dx, dy }) => {
        if (mobile && kind === 'sketch' && order > 4) {
          item.style.opacity = '0';
          return;
        }

        const revealStart = kind === 'idea'
          ? 0.23 + (order - 1) * 0.065
          : 0.40 + (order - 1) * 0.045;
        const revealEnd = revealStart + 0.12;
        const reveal = smoothstep(revealStart, revealEnd, normalized);
        const steppedReveal = kind === 'idea' ? Math.floor(reveal * 18) / 18 : reveal;
        const itemOpacity = reveal * (1 - dissolve) * opacity;
        const gatherScale = 1 - gather * (kind === 'idea' ? 0.68 : 0.64);
        const revealScale = 0.92 + reveal * 0.08;
        const translateX = dx * handoffGather;
        const translateY = dy * handoffGather;
        const activeRotation = rotation * (1 - gather);

        const revealRight = `${((1 - steppedReveal) * 100).toFixed(2)}%`;
        const opacityValue = itemOpacity.toFixed(3);
        const filter = `blur(${(filterGather * 0.9).toFixed(2)}px) brightness(${(1 + filterGather * 0.68).toFixed(3)})`;
        const transform = `translate(-50%, -50%) translate3d(${translateX.toFixed(1)}px, ${translateY.toFixed(1)}px, 0) rotate(${activeRotation.toFixed(2)}deg) scale(${(revealScale * gatherScale).toFixed(3)})`;
        const state = `${revealRight}|${opacityValue}|${filter}|${transform}`;

        if (this.itemRenderStates.get(item) !== state) {
          item.style.setProperty('--story-reveal-right', revealRight);
          item.style.opacity = opacityValue;
          item.style.filter = filter;
          item.style.transform = transform;
          this.itemRenderStates.set(item, state);
        }
      });

      const coreReveal = smoothstep(0.70, 0.92, normalized);
      const coreExit = smoothstep(0.975, 1, normalized);
      const coreProgress = coreReveal * (1 - coreExit);
      if (this.core) {
        const opacity = coreProgress.toFixed(3);
        const transform = `translate(-50%, -50%) scale(${(0.5 + coreProgress * 0.5).toFixed(3)})`;
        const state = `${opacity}|${transform}`;
        if (this.coreRenderState !== state) {
          this.core.style.opacity = opacity;
          this.core.style.transform = transform;
          this.coreRenderState = state;
        }
      }

      const handoffReveal = smoothstep(0.82, 0.94, normalized);
      const handoffExit = smoothstep(0.965, 1, normalized);
      const handoffProgress = handoffReveal * (1 - handoffExit);
      if (this.handoff) {
        const opacity = handoffProgress.toFixed(3);
        const transform = `translate3d(0, ${((1 - handoffProgress) * 10).toFixed(1)}px, 0)`;
        const state = `${opacity}|${transform}`;
        if (this.handoffRenderState !== state) {
          this.handoff.style.opacity = opacity;
          this.handoff.style.transform = transform;
          this.handoffRenderState = state;
        }
      }

      const storyState = normalized >= 0.98
        ? 'gathered'
        : normalized >= 0.62
          ? 'gathering'
          : normalized >= 0.40
            ? 'sketches'
            : normalized > 0.02
              ? 'ideas'
              : 'waiting';
      if (this.root.dataset.storyState !== storyState) this.root.dataset.storyState = storyState;
    }

    destroy() {
      this.destroyed = true;
      cancelAnimationFrame(this.frame);
      this.viewportObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.root.classList.remove('is-motion-active');
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      this.mobileQuery.removeEventListener?.('change', this.onViewportChange);
      delete this.root._moztechIlluminatedJourney;
    }
  }

  const init = (scope = document) => {
    const roots = [];
    if (scope.matches?.('[data-moztech-illuminated-story]')) roots.push(scope);
    scope.querySelectorAll?.('[data-moztech-illuminated-story]').forEach((root) => roots.push(root));

    roots.forEach((root) => {
      if (root._moztechIlluminatedJourney) return;
      root._moztechIlluminatedJourney = new MoztechIlluminatedJourneyInstance(root);
    });
  };

  const destroy = (scope) => {
    const root = scope.matches?.('[data-moztech-illuminated-story]')
      ? scope
      : scope.querySelector?.('[data-moztech-illuminated-story]');
    root?._moztechIlluminatedJourney?.destroy();
  };

  window.MoztechIlluminatedJourney = { version: VERSION, init, destroy };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
  document.addEventListener('shopify:section:select', (event) => {
    const root = event.target.matches?.('[data-moztech-illuminated-story]')
      ? event.target
      : event.target.querySelector?.('[data-moztech-illuminated-story]');
    if (!root?._moztechIlluminatedJourney?.isDesignMode) return;
    root._moztechIlluminatedJourney.apply(clamp(Number(root.dataset.storyEditorProgress) || 0.52));
  });
})();
